import React from 'react';
import { Heart, Shield, Mail, Instagram, Twitter, MessageCircle, BookOpen, Activity, PlayCircle, ShieldCheck, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export function Footer() {
    const { t } = useTranslation();

    return (
        <footer className="bg-slate-900 border-t border-slate-800 pt-24 pb-12 px-6 overflow-hidden relative">
            
            {/* Background Decorative Element */}
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-64 h-64 bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none" />
            
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-20">
                    
                    {/* Brand Section */}
                    <div className="lg:col-span-5 space-y-8">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full overflow-hidden shadow-xl shadow-emerald-500/10 border border-slate-700/50 bg-white">
                                <img src="/logo.png" alt="ManoSathi" className="w-full h-full object-cover" />
                            </div>
                            <span className="font-black text-3xl text-white tracking-tighter">{t('app_name')}</span>
                        </div>
                        <p className="text-slate-400 text-lg font-medium leading-relaxed max-w-md">
                            {t('footer.desc')}
                        </p>
                        <div className="flex gap-4">
                            {[Instagram, Twitter, Mail].map((Icon, i) => (
                                <button key={i} className="w-11 h-11 rounded-2xl bg-slate-800 text-slate-400 hover:bg-emerald-500 hover:text-white border border-slate-700 hover:border-emerald-400 transition-all duration-300 flex items-center justify-center group active:scale-90 shadow-lg">
                                    <Icon className="w-5 h-5" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Columns */}
                    <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-12">
                        <div className="space-y-6">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('footer.ecosystem')}</h4>
                            <ul className="space-y-4">
                                {[
                                    { name: t('nav.chat'), path: '/chat' },
                                    { name: t('footer.journal'), path: '/journal' },
                                    { name: t('footer.breathe'), path: '/calm-down' },
                                    { name: t('footer.checkin'), path: '/quiz' }
                                ].map((link, i) => (
                                    <li key={i}><a href={link.path} className="text-sm font-bold text-slate-400 hover:text-white transition-colors">{link.name}</a></li>
                                ))}
                            </ul>
                        </div>
                        <div className="space-y-6">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('footer.resources')}</h4>
                            <ul className="space-y-4 text-sm font-bold text-slate-400">
                                <li><a href="/pricing" className="hover:text-emerald-400 transition-colors">{t('footer.pro')}</a></li>
                                <li><a href="#" className="hover:text-emerald-400 transition-colors text-rose-400">{t('footer.sos')}</a></li>
                                <li><a href="/clinical-validation" className="hover:text-emerald-400 transition-colors">{t('footer.clinical')}</a></li>
                                <li><a href="#" className="hover:text-emerald-400 transition-colors">{t('footer.therapist')}</a></li>
                            </ul>
                        </div>
                        <div className="space-y-6">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('footer.legal')}</h4>
                            <ul className="space-y-4 text-sm font-bold text-slate-400">
                                <li><a href="#" className="hover:text-slate-200 transition-colors">{t('footer.privacy')}</a></li>
                                <li><a href="#" className="hover:text-slate-200 transition-colors">{t('footer.terms')}</a></li>
                                
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar High-Trust */}
                <div className="pt-12 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-8">
                    
                    {/* Trust Seals */}
                    <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-50 grayscale hover:grayscale-0 transition-all duration-700">
                        <div className="flex items-center gap-2.5">
                            <ShieldCheck className="w-5 h-5 text-emerald-500" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">{t('footer.secure')}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">{t('footer.iso')}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <Shield className="w-5 h-5 text-emerald-500" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">{t('footer.gdpr')}</span>
                        </div>
                    </div>

                    <div className="text-center md:text-right space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">{t('footer.copyright')}</p>
                        <p className="text-[8px] font-bold text-slate-700 uppercase tracking-widest">{t('footer.global')}</p>
                    </div>
                </div>
            </div>
            
            {/* Ambient Base Glow */}
            <div className="absolute bottom-0 inset-x-0 h-1/4 bg-gradient-to-t from-emerald-500/5 to-transparent pointer-events-none" />
        </footer>
    );
}
